/*
 * Which rows the asset's History list shows, and which row opens a given visit (MMG-4345).
 *
 * History spans job types, so the same visit can arrive twice: once as the closed job's measurement
 * point result and once as that job's completed work order. Only one of them may be listed, and it
 * has to be the one that can show the visit's own measurement points:
 *
 *   - a work order row has no list of its own, so clicking it renders the visit against the current
 *     job's measurement points - right only when the two share a list type
 *   - a result row knows the list its answers were recorded under, so it can render any type
 *     (selectCrossJobTypeHistoricalList)
 *
 * So a work order row is listed for the current job's own type, and every other type comes through
 * its result row.
 *
 * A result row is dropped only when a work order row for that same job is really on the list.
 * "The work order covers it" cannot be assumed: work orders reach the store from the jobs opened
 * this session, never from Load History - the API marks MeasurementPointListResult.Job [JsonIgnore],
 * so the nested work orders the history endpoint Includes are never serialized. Assuming coverage
 * hid a repair job's own past repair visits completely, against the ticket's "allowing for any
 * CLOSED job to populate in this history".
 */

import { IWorkOrder, ImeasurementPointResultWithType, Ijob } from '../../models';
import { MPLTypeByWorkOrderType, MPLTypebyJobTypesID, jobTypesIdEnum, workOrderTypesEnum } from '../../models-enums';

/*
 * The job types whose History lists their completed work orders. Deliberately not
 * isWorkOrderTypeJob: that counts warranty BM as well, and History has never listed work order rows
 * on a warranty BM job.
 */
const jobListsWorkOrders = (jobTypeID: Ijob['jobTypeID']): boolean =>
    jobTypeID === jobTypesIdEnum.repair ||
    jobTypeID === jobTypesIdEnum.maintenance ||
    jobTypeID === jobTypesIdEnum.servicePlan ||
    jobTypeID === jobTypesIdEnum.commissioning;

/*
 * The measurement point list type a job renders.
 */
const listTypeForJobType = (jobTypeID: Ijob['jobTypeID']): number => MPLTypebyJobTypesID[jobTypeID];

export const isHistoryWorkOrder = (row: IWorkOrder | ImeasurementPointResultWithType): row is IWorkOrder =>
    'vendor' in row && 'closingNotes' in row;

export const showsHistoryWorkOrderRow = (workOrder: IWorkOrder, jobTypeID: Ijob['jobTypeID']): boolean =>
    jobListsWorkOrders(jobTypeID) &&
    MPLTypeByWorkOrderType[workOrder.type as workOrderTypesEnum] === listTypeForJobType(jobTypeID);

/*
 * The jobs whose visit is already on the list as a work order row, so their result row would be a
 * second row for the same visit. `jobIDByWorkOrderID` comes from the job/work order links in the
 * store - IWorkOrder itself does not carry its job.
 */
export const jobIDsCoveredByWorkOrderRows = (
    history: Array<IWorkOrder | ImeasurementPointResultWithType>,
    jobTypeID: Ijob['jobTypeID'],
    jobIDByWorkOrderID: { [workOrderID: string]: string }
): Set<string> => {
    const covered = new Set<string>();

    history.forEach(row => {
        if (!isHistoryWorkOrder(row) || !showsHistoryWorkOrderRow(row, jobTypeID)) {
            return;
        }

        const jobID = jobIDByWorkOrderID[row.id];

        if (jobID) {
            covered.add(jobID);
        }
    });

    return covered;
};

export const showsHistoryResultRow = (
    result: ImeasurementPointResultWithType,
    jobTypeID: Ijob['jobTypeID'],
    jobIDsOnWorkOrderRows: ReadonlySet<string>
): boolean =>
    !(
        jobListsWorkOrders(jobTypeID) &&
        result.measurementPointListType === listTypeForJobType(jobTypeID) &&
        jobIDsOnWorkOrderRows.has(result.jobID)
    );
